import { useCallback, useMemo, useState } from 'react';

import {
  type ErrorLike,
  type InternalRefetchQueriesInclude,
} from '@apollo/client';
import { useApolloClient, useMutation, useQuery } from '@apollo/client/react';
import { Box, Checkbox } from '@mui/material';
import {
  DataGridPremium,
  type GridColDef,
  type GridRowOrderChangeParams,
  type GridRowsProp,
} from '@mui/x-data-grid-premium';

import { gql } from '@graphql/__generated__';
import {
  type CreatePropertyFileMutation,
  type PropertyFileFragment,
} from '@graphql/__generated__/graphql';
import { FileDeleteDialog } from '@modules/_shared/components/FileDeleteDialog';
import {
  FILE_GRID_SKELETON_SLOT_PROPS,
  FILE_GRID_SX,
  FILE_UPLOAD_STICKY_SX,
} from '@modules/_shared/components/fileGridStyles';
import {
  FileTypeAutocomplete,
  GET_DOCUMENT_TYPES,
} from '@modules/_shared/components/FileTypeAutocomplete';
import { FileViewerDialog } from '@modules/_shared/components/FileViewerDialog';
import { MutationErrorSnackbar } from '@modules/_shared/components/MutationErrorModal';
import { TimeAgo } from '@modules/_shared/components/TimeAgo';
import { UploadFileCard } from '@modules/_shared/components/UploadCard';
import {
  type EntityFileUploadData,
  useEntityFileUpload,
} from '@modules/_shared/hooks/useEntityFileUpload';
import {
  useFileActionsDef,
  useFileDef,
  useUploaderDef,
} from '@modules/_shared/utils/files/fileDefs';
import { useIsDragging } from '@modules/_shared/utils/mouseEvents';
import {
  CREATE_PROPERTY_FILE,
  REORDER_PROPERTY_FILES,
  UPDATE_FILE,
  UPDATE_PROPERTY_FILE,
} from '@modules/listings/lotsQueries';
import { useLocale } from '@shared/hooks/locale';

export type PropertyFilesColumns =
  | 'file'
  | 'document_type'
  | 'uploader'
  | 'created'
  | 'is_visible'
  | 'display_on_listing';

type PropertyFilesProps = {
  propertyId?: string;
  files: PropertyFileFragment[];
  refetchQueries?: InternalRefetchQueriesInclude;
  onDelete?: (id: string) => void;
  loading?: boolean;
  readOnly?: boolean;
  hideColumns?: PropertyFilesColumns[];
  onUpdate?: () => void;
  acceptedMimetypes?: string[];
  acceptLabel?: string;
  hideUpload?: boolean;
  onSelectFile?: (fileId: string) => void;
  selectedFileId?: string | null;
};

type PropertyFileRow = PropertyFileFragment & { __isPending?: boolean };

export const PROPERTY_FILES_FRAGMENT = gql(/* GraphQL */ `
  fragment PropertyFile on property_files {
    property_id
    file_id
    is_visible
    display_on_listing
    order_nr
    file {
      ...File
    }
  }
`);

export const UPDATE_PROPERTY_FILE_DISPLAY_ON_LISTING = gql(/* GraphQL */ `
  mutation UpdatePropertyFileDisplayOnListing(
    $display: Boolean!
    $file_id: uuid!
    $property_id: uuid!
    $should_reset_non_video: Boolean!
    $should_reset_video: Boolean!
  ) {
    update_property_file: update_property_files(
      _set: { display_on_listing: $display }
      where: { file_id: { _eq: $file_id }, property_id: { _eq: $property_id } }
    ) {
      returning {
        property_id
        file_id
        display_on_listing
      }
    }
    reset_property_files: update_property_files(
      _set: { display_on_listing: false }
      where: {
        file_id: { _neq: $file_id }
        property_id: { _eq: $property_id }
        file: {
          _or: [
            { _not: { document_type: {} } }
            { document_type: { name: { _neq: "video" } } }
          ]
        }
      }
    ) @include(if: $should_reset_non_video) {
      returning {
        property_id
        file_id
        display_on_listing
      }
    }
    reset_property_files_video: update_property_files(
      _set: { display_on_listing: false }
      where: {
        file_id: { _neq: $file_id }
        property_id: { _eq: $property_id }
        file: { document_type: { name: { _eq: "video" } } }
      }
    ) @include(if: $should_reset_video) {
      returning {
        property_id
        file_id
        display_on_listing
      }
    }
  }
`);

const VIDEOS_MIMETYPES = [
  'video/mp4', // MP4
  'video/x-msvideo', // AVI
  'video/quicktime', // MOV
  'video/x-ms-wmv', // WMV
  'video/x-flv', // FLV
  'video/x-matroska', // MKV
  'video/3gpp', // 3GP, most browsers use this
  'video/3gp', // sometimes required for legacy support
];

// IDX only allows pdf/rtf/doc/ppt/docx/pptx for files
export const ALLOWED_MIMETYPES_FOR_PUBLICATION = [
  // Files
  'application/pdf',
  'application/rtf',
  'text/rtf',
  'application/x-rtf',
  'application/msword',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  // Images
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  // Videos
  ...VIDEOS_MIMETYPES,
];

export const isMimetypeAllowedForPublication = (
  mimetype: string | null | undefined,
): boolean => {
  if (mimetype == null) {
    return false;
  }
  return ALLOWED_MIMETYPES_FOR_PUBLICATION.includes(mimetype);
};

export const isMimetypeVideo = (
  mimetype: string | null | undefined,
): boolean => {
  if (mimetype == null) {
    return false;
  }
  return VIDEOS_MIMETYPES.includes(mimetype);
};

export const isVideoFile = (file: {
  mimetype?: string | null;
  document_type?: { name?: string | null } | null;
}): boolean =>
  isMimetypeVideo(file.mimetype) || file.document_type?.name === 'video';

export const PropertyFiles: React.FC<PropertyFilesProps> = ({
  propertyId,
  files,
  refetchQueries,
  onDelete,
  loading = false,
  readOnly = false,
  hideColumns,
  onUpdate,
  acceptedMimetypes = ALLOWED_MIMETYPES_FOR_PUBLICATION,
  acceptLabel,
  hideUpload = false,
  onSelectFile,
  selectedFileId,
}) => {
  const { t } = useLocale();
  const client = useApolloClient();
  const [dataGridEl, setDataGridEl] = useState<HTMLElement | null>(null);
  const isDraggingRows = useIsDragging({ target: dataGridEl });
  const [mutationError, setMutationError] = useState<ErrorLike | null>(null);
  const [viewerFile, setViewerFile] = useState<
    PropertyFileFragment['file'] | null
  >(null);
  const [deleteFile, setDeleteFile] = useState<PropertyFileFragment | null>(
    null,
  );

  const { data: documentTypesData } = useQuery(GET_DOCUMENT_TYPES, {
    variables: { types: ['property'] },
  });

  const [updateFile] = useMutation(UPDATE_FILE, {
    onError: setMutationError,
    refetchQueries,
  });

  const [updatePropertyFile] = useMutation(UPDATE_PROPERTY_FILE, {
    onError: setMutationError,
  });

  const [updatePropertyFileDisplayOnListing] = useMutation(
    UPDATE_PROPERTY_FILE_DISPLAY_ON_LISTING,
    {
      onError: setMutationError,
    },
  );

  const [reOrderPropertyFiles] = useMutation(REORDER_PROPERTY_FILES, {
    onError: setMutationError,
    refetchQueries,
  });

  const [createPropertyFile] = useMutation(CREATE_PROPERTY_FILE, {
    onError: setMutationError,
  });

  const videoDocumentTypeId = useMemo(() => {
    return (documentTypesData?.document_types ?? []).find(
      type => type.name === 'video',
    )?.id;
  }, [documentTypesData?.document_types]);

  const createFile = useCallback(
    (
      file: EntityFileUploadData,
      onCompleted: (data: CreatePropertyFileMutation) => void,
      onError: (error: ErrorLike) => void,
    ) => {
      if (propertyId == null) {
        return;
      }

      createPropertyFile({
        variables: {
          object: {
            file: {
              data: {
                ...file,
                // We force the document type to video if the mimetype is a video so that we can later identify it
                ...(isMimetypeVideo(file.mimetype) &&
                videoDocumentTypeId != null
                  ? {
                      document_type_id: videoDocumentTypeId,
                    }
                  : {}),
              },
            },
            property_id: propertyId,
          },
        },
        onCompleted,
        onError,
        update: (cache, { data }) => {
          if (data?.insert_property_files_one == null) {
            return;
          }

          const propertyIdentifier = cache.identify({
            __typename: 'properties',
            id: propertyId,
          });

          cache.modify({
            id: propertyIdentifier,
            fields: {
              property_files(existingFiles = []) {
                // Write the full property_file fragment to cache
                const newFileRef = cache.writeFragment({
                  data: data.insert_property_files_one,
                  fragment: PROPERTY_FILES_FRAGMENT,
                  fragmentName: 'PropertyFile',
                });

                return [...existingFiles, newFileRef];
              },
            },
          });
        },
      });
    },
    [createPropertyFile, propertyId, videoDocumentTypeId],
  );

  const { upload, pendingFiles, FileErrors } =
    useEntityFileUpload<CreatePropertyFileMutation>({
      createFile,
    });

  // Combine regular files with pending files
  const propertyFiles: GridRowsProp<PropertyFileRow> = useMemo(
    () => [
      ...files,
      // Transform pending files into PropertyFileFragment-like structure
      ...pendingFiles.map((file, index) => {
        const file_id = `pending-file-${file.name}-${file.size}`;
        const maxOrderNr = Math.max(
          ...files.map(file => file.order_nr ?? 0),
          0,
        );

        return {
          property_id: propertyId ?? '',
          file_id,
          is_visible: false,
          display_on_listing: false,
          order_nr: maxOrderNr + index + 1,
          file: {
            id: file_id,
            name: file.name,
            mimetype: file.type,
            url: '',
            size: file.size,
            document_type: null,
            uploader: null,
            created_at: new Date().toISOString(),
          },
          __isPending: true, // Flag to identify pending files
        };
      }),
    ],
    [files, pendingFiles, propertyId],
  );

  const handleRowOrderChange = async (
    { row, targetIndex }: GridRowOrderChangeParams,
    propId: string,
  ) => {
    await reOrderPropertyFiles({
      variables: {
        property_id: propId,
        file_id: row.file.id,
        target_position: propertyFiles[targetIndex].order_nr,
      },
    });
  };

  const onFilesSelected = useCallback(
    (files: File[]) => {
      const propertyIdentifier = client.cache.identify({
        __typename: 'properties',
        id: propertyId,
      });

      let someFilesNotUpdated = false;

      Promise.allSettled(
        files.map(file =>
          upload(file).then(response => {
            if (
              response?.insert_property_files_one != null &&
              propertyIdentifier != null
            ) {
              try {
                client.cache.modify({
                  id: propertyIdentifier,
                  fields: {
                    property_files(existingFiles = []) {
                      // Write the full property_file fragment to cache
                      const newFileRef = client.cache.writeFragment({
                        data: {
                          ...response.insert_property_files_one,
                          display_on_listing: false, // Default value
                        },
                        fragment: PROPERTY_FILES_FRAGMENT,
                        fragmentName: 'PropertyFile',
                      });

                      return [...existingFiles, newFileRef];
                    },
                  },
                });
              } catch {
                someFilesNotUpdated = true;
              }
            }
          }),
        ),
      ).then(results => {
        const successfulUploads = results.filter(
          result => result.status === 'fulfilled',
        );

        if (successfulUploads.length > 0 && someFilesNotUpdated) {
          // Fallback to refetch if cache update fails for some files
          if (refetchQueries != null) {
            const queriesToRefetch = Array.isArray(refetchQueries)
              ? refetchQueries.map(item =>
                  typeof item === 'object' && 'query' in item
                    ? item.query
                    : item,
                )
              : refetchQueries;
            client.refetchQueries({ include: queriesToRefetch });
          }
        }
      });
    },
    [upload, propertyId, client, refetchQueries],
  );

  const fileDef = useFileDef<PropertyFileRow>({
    disableLink: onSelectFile != null,
  });
  const uploaderDef = useUploaderDef<PropertyFileRow>();
  const fileActionsDef = useFileActionsDef<PropertyFileRow>({
    setViewerFile,
    setDeleteFile,
    deleteFile,
    showInMenu: true,
    props: {
      align: 'right',
    },
  });

  // Check if a row is pending
  const isPendingRow = (row: PropertyFileRow) => row.__isPending === true;

  let columns: GridColDef<PropertyFileRow>[] = [
    fileDef,
    {
      field: 'document_type',
      headerName: t('Document type'),
      display: 'flex',
      flex: 1,
      minWidth: 150,
      renderCell: ({ row }) => {
        const isPending = isPendingRow(row);
        return (
          <FileTypeAutocomplete
            types={['property']}
            value={row.file.document_type ?? undefined}
            onChange={(_, value) => {
              if (!isPending && !readOnly) {
                updateFile({
                  variables: {
                    id: row.file.id,
                    input: { document_type_id: value.id },
                  },
                });
              }
            }}
            disabled={
              readOnly || isPending || row.file.document_type?.name === 'video'
            }
          />
        );
      },
      sortable: false,
    },
    uploaderDef,
    {
      field: 'created',
      flex: 1,
      minWidth: 150,
      headerName: t('Uploaded'),
      renderCell: ({ row }) =>
        isPendingRow(row) ? null : (
          <TimeAgo dateString={row.file.created_at} addSuffix />
        ),
      sortable: false,
    },
    {
      field: 'is_visible',
      flex: 1,
      minWidth: 150,
      headerName: t('Attach to brochure'),
      renderCell: ({ row }) => {
        const isPending = isPendingRow(row);
        return (
          <Checkbox
            size="small"
            sx={{
              '&.MuiCheckbox-root': {
                padding: '4px 10px',
              },
            }}
            disableRipple
            disabled={
              readOnly ||
              isPending ||
              (row.file.mimetype !== 'application/pdf' &&
                !row.file.mimetype?.startsWith('image/'))
            }
            checked={row.is_visible}
            onChange={evt => {
              if (propertyId == null || isPending || readOnly) {
                return;
              }

              updatePropertyFile({
                variables: {
                  file_id: row.file.id,
                  property_id: propertyId,
                  input: { is_visible: evt.target.checked },
                },
              });
              onUpdate?.();
            }}
          />
        );
      },
      sortable: false,
    },
    {
      field: 'display_on_listing',
      flex: 1,
      minWidth: 150,
      headerName: t('Display on portals'),
      renderCell: ({ row }) => {
        const isPending = isPendingRow(row);
        return (
          <Checkbox
            size="small"
            sx={{
              '&.MuiCheckbox-root': {
                padding: '4px 10px',
              },
            }}
            disableRipple
            disabled={
              readOnly ||
              isPending ||
              !isMimetypeAllowedForPublication(row.file.mimetype)
            }
            checked={row.display_on_listing}
            onChange={evt => {
              if (propertyId == null || isPending || readOnly) {
                return;
              }

              // For now we only allow one file to be displayed on the listing at a time
              const isVideoDocumentType =
                row.file.document_type?.name === 'video';
              const shouldResetVideo =
                evt.target.checked && isVideoDocumentType;
              const shouldResetNonVideo =
                evt.target.checked && !isVideoDocumentType;

              updatePropertyFileDisplayOnListing({
                variables: {
                  file_id: row.file.id,
                  property_id: propertyId,
                  display: evt.target.checked,
                  should_reset_non_video: shouldResetNonVideo,
                  should_reset_video: shouldResetVideo,
                },
              });
              onUpdate?.();
            }}
          />
        );
      },
      sortable: false,
    },
    fileActionsDef,
  ];

  if (readOnly) {
    columns = columns.filter(column => column.field !== 'actions');
  }

  columns = columns.filter(
    column =>
      hideColumns == null ||
      !hideColumns.includes(column.field as PropertyFilesColumns),
  );

  const rowSelectionModel = useMemo(
    () => ({
      type: 'include' as const,
      ids: new Set(selectedFileId != null ? [selectedFileId] : []),
    }),
    [selectedFileId],
  );

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        overflow: 'auto',
        height: '100%',
        position: 'relative',
      }}
    >
      <FileViewerDialog
        open={viewerFile != null}
        url={viewerFile?.url ?? ''}
        name={viewerFile?.name ?? ''}
        onClose={() => setViewerFile(null)}
      />
      <FileDeleteDialog
        open={deleteFile != null}
        onDelete={onDelete}
        fileId={deleteFile?.file.id ?? ''}
        onClose={() => setDeleteFile(null)}
      />
      {!readOnly && !hideUpload && (
        <Box sx={FILE_UPLOAD_STICKY_SX}>
          <UploadFileCard
            onSelect={onFilesSelected}
            noMinHeight
            disableDrop={isDraggingRows}
            accept={acceptedMimetypes.join(', ')}
            acceptLabel={
              acceptLabel ??
              t('PDF/RTF/Word/PowerPoint files and JPEG/PNG/GIF/WEBP images')
            }
          />
        </Box>
      )}
      <div css={{ display: 'flex', flexDirection: 'column' }}>
        <DataGridPremium
          slotProps={FILE_GRID_SKELETON_SLOT_PROPS}
          sx={FILE_GRID_SX}
          ref={ref => {
            setDataGridEl(ref);
          }}
          rows={propertyFiles}
          columns={columns}
          loading={loading}
          rowReordering={!readOnly}
          onRowOrderChange={newModel => {
            if (
              propertyId != null &&
              !isPendingRow(newModel.row as PropertyFileFragment)
            ) {
              handleRowOrderChange(newModel, propertyId);
              onUpdate?.();
            }
          }}
          disableMultipleRowSelection
          disableColumnReorder
          disableRowSelectionOnClick
          getRowId={row => row.file.id}
          rowHeight={64}
          rowSelectionModel={rowSelectionModel}
          onCellClick={({ field, row }) => {
            const fileRow = row as PropertyFileFragment;
            if (field !== 'file' || isPendingRow(fileRow)) {
              return;
            }

            if (onSelectFile != null) {
              onSelectFile(fileRow.file.id);
              return;
            }

            const hasViewer = fileRow.file.mimetype === 'application/pdf';

            if (hasViewer) {
              setViewerFile(fileRow.file);
            }
          }}
        />
      </div>
      <FileErrors />
      <MutationErrorSnackbar
        error={mutationError}
        onClose={() => setMutationError(null)}
      />
    </Box>
  );
};
